import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  imageUrl?: string;
  url?: string;
  type?: 'website' | 'article';
  ogTitle?: string;
  ogDescription?: string;
  structuredData?: Record<string, unknown> | Array<Record<string, unknown>>;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  imageUrl,
  url,
  type = 'website',
  ogTitle,
  ogDescription,
  structuredData,
}) => {
  const siteName = 'mapleos';
  const siteUrl = 'https://mapleos.ai';
  const normalizedTitle = title.trim();
  const fullTitle = normalizedTitle.toLowerCase().includes(siteName) ? normalizedTitle : `${normalizedTitle} | ${siteName}`;
  const fullUrl = url ? `${siteUrl}${url}` : siteUrl;
  const fullImageUrl = imageUrl
    ? imageUrl.startsWith('http')
      ? imageUrl
      : `${siteUrl}${imageUrl}`
    : `${siteUrl}/images/mapleos-screenshot.png`;
  const normalizedOgTitle = (ogTitle || normalizedTitle).trim();
  const fullOgTitle = normalizedOgTitle.toLowerCase().includes(siteName) ? normalizedOgTitle : `${normalizedOgTitle} | ${siteName}`;
  const normalizedOgDescription = (ogDescription || description).trim();

  React.useEffect(() => {
    document.dispatchEvent(new Event('prerender-ready'));
  }, [title, description, keywords, imageUrl, url, type, ogTitle, ogDescription, structuredData]);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <link rel="canonical" href={fullUrl} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content="index,follow" />

      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullOgTitle} />
      <meta property="og:description" content={normalizedOgDescription} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:alt" content={fullOgTitle} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullOgTitle} />
      <meta name="twitter:description" content={normalizedOgDescription} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:image:alt" content={fullOgTitle} />
      <meta name="twitter:site" content="@mapleosai" />

      {structuredData && (
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      )}
    </Helmet>
  );
};

export default SEO;
